import {recordRumAction} from '../../../util/datadog/datadogRum';
import {
  getBlobWithRetry,
  isAbortError,
  type RetrySpec,
} from '../../../util/requests';
import {
  createTileMediaError,
  describeHttpFailure,
  GENERIC_MEDIA_BLOB_FAILURE_MESSAGE,
  isExpiredUrlError,
  TileMediaError,
  tileMediaErrorTypeFromHttpFailure,
} from './errors';
import IN_MEMORY_CACHE from './inMemoryCache';

// Tile blob fetches to cloud storage fail in synchronized bursts when the
// browser's connection to the storage host is cut, and a fresh connection
// typically succeeds. Two short retries recover from that without holding a
// tile in the loading state for long. 4xx responses (expired or rejected
// signed URLs) are not retried at all; see `fetchWithRetry`.
export const BLOB_RETRY: RetrySpec = {
  maxRetries: 2,
  calcDelay: (n: number) => 500 * Math.pow(2, n),
};

export interface FetchBlobOptions {
  // A blob fetched only to back the download button (e.g. a video that is
  // already streaming) is not a user-visible failure, so callers can keep it
  // out of the error log and report it separately.
  logFailure?: boolean;
}

/**
 * A blob that only backs the download button of an already-streaming video
 * failed. Not a user-visible media error, so it is reported as its own RUM
 * action instead of being logged as one.
 */
export function recordBackgroundBlobFetchFailure(
  mediaType: string,
  errorMessage: string
): void {
  recordRumAction('mediaBrowserPanelTile.backgroundBlobFetchFailed', {
    mediaType,
    errorMessage,
  });
}

/**
 * A signed URL was rejected as expired. Callers refresh the URL and retry,
 * so this is not a media error; it is recorded so the rate stays visible.
 */
export function recordExpiredBlobUrl(): void {
  recordRumAction('mediaBrowserPanelTile.blobUrlExpired', {});
}

/**
 * Fetch blob bytes without the session-wide URL promise cache. Used by the
 * workspace blob store, which owns in-flight and LRU caching by file path key.
 * Legacy callers should use `fetchFileBlob` so `BLOB_PROMISE_BY_URL` stays hot.
 */
export async function fetchBlobBytes(
  directURL: string,
  {logFailure = true}: FetchBlobOptions = {}
): Promise<Blob> {
  return getBlobWithRetry(directURL, {retry: BLOB_RETRY}).catch(err => {
    // An abort means the page is unloading or the caller cancelled; it is not
    // a media failure, so don't log it or wrap it as one.
    if (isAbortError(err)) {
      throw err;
    }
    if (isExpiredUrlError(err)) {
      recordExpiredBlobUrl();
    } else if (logFailure) {
      console.error(
        `Error fetching file blob: ${describeHttpFailure(err) ?? err}`
      );
    }
    if (err instanceof TileMediaError) {
      throw err;
    }
    throw createTileMediaError(
      GENERIC_MEDIA_BLOB_FAILURE_MESSAGE,
      tileMediaErrorTypeFromHttpFailure(err),
      err instanceof Error ? err : undefined
    );
  });
}

export async function fetchFileBlob(
  directURL: string,
  options?: FetchBlobOptions
): Promise<Blob | null> {
  return await fetchBlobDeduped(directURL, options);
}

async function fetchBlobDeduped(
  url: string,
  options?: FetchBlobOptions
): Promise<Blob> {
  const existingPromise = IN_MEMORY_CACHE.BLOB_PROMISE_BY_URL.get(url);
  if (existingPromise) {
    return existingPromise;
  }

  // getBlobWithRetry returns a Promise synchronously (no internal awaits before
  // the Promise object is created), so we store it in the cache before any
  // async work begins. Concurrent calls for the same URL will find it on their
  // first cache check and share this Promise — no double-check needed.
  const blobPromise = fetchBlobBytes(url, options);

  IN_MEMORY_CACHE.BLOB_PROMISE_BY_URL.set(url, blobPromise);
  // A cached rejection would otherwise be handed to every later caller, so a
  // page restored from bfcache could never refetch this URL. Genuine failures
  // stay cached so a broken URL isn't refetched on every render.
  blobPromise.catch(err => {
    if (
      isAbortError(err) &&
      IN_MEMORY_CACHE.BLOB_PROMISE_BY_URL.get(url) === blobPromise
    ) {
      IN_MEMORY_CACHE.BLOB_PROMISE_BY_URL.delete(url);
    }
  });
  return blobPromise;
}

export function getObjectURLForBlob(b: Blob): string {
  const objectURL =
    IN_MEMORY_CACHE.OBJECT_URL_BY_BLOB.get(b) ?? URL.createObjectURL(b);
  IN_MEMORY_CACHE.OBJECT_URL_BY_BLOB.set(b, objectURL);
  return objectURL;
}
